import { Box, Grid } from '@mui/material';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import usePageTitle from 'hooks/usePageTitle';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import QuestionnaireItem from 'FeatureModules/Questionnaire/Components/QuestionnaireResponseTable/QuestionnaireItem';
import { EQuestionnaireResponseHeaderColumnEnum } from 'FeatureModules/Questionnaire/Components/QuestionnaireResponseTable/questionnaireResponseHeaderColumns';
import QuestionnaireResponseTable from 'FeatureModules/Questionnaire/Components/QuestionnaireResponseTable/QuestionnaireResponseTable';
import { EmptyStateCard, ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useQuestionnaire from 'FeatureModules/Questionnaire/hooks/useQuestionnaire';
import React, { FC } from 'react';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { SeeAllLink } from '../../Partner/Pages/PartnerOverview';
import RiskPieChart from 'DesignSystem/DataDisplay/RiskPie/RiskPieChart';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';

const QuestionnaireOverview: FC = () => {
  usePageTitle('Questionnaire  overview');

  const {
    questionnaire,
    error,
    loading,
    filters,
    questionnaireResponsesLoading,
    questionnaireResponses,
    questionnaireResponseSummary,
    pendingQuestionnairesRequests,
    hasCompletedResponses,
    handleEditQuestionnaire,
    handlePreviewQuestions,
    handleViewResponse,
    handleCreateQuestionnaireRequest,
    handleEditRequest,
    handleDeleteRequest,
    handleFilterChange,
    handleFilterReset,
    handleAcceptRequest,
  } = useQuestionnaire();

  if (loading) {
    return <Loader />;
  }
  if (error || !questionnaire) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <PageTitle
        title="Questionnaire overview"
        goBackLabel="All questionnaires"
        goBackUrl="/questionnaires"
        titleExtension={questionnaire?.title}
      />
      <Box mb={3} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <OverviewHeader placeholderIconName="questionnaire-placeholder">
            <Box
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              height="100%"
              justifyContent="space-evenly"
            >
              <FlexBox>
                <Box mb={1}>
                  <ThemeTypography variant="TITLE_MEDIUM" autoOverflow maxWidth={550}>
                    {questionnaire.title}
                  </ThemeTypography>
                </Box>

                {questionnaire.externalId && (
                  <FlexBox ml={2}>
                    <ThemeTypography variant="TITLE_EXTRA_SMALL" color="GRAY_40">
                      |
                    </ThemeTypography>
                    <Box mr={1} />
                    <ThemeTypography
                      variant="TITLE_EXTRA_SMALL"
                      autoOverflow
                      maxWidth={200}
                      color="GRAY_40"
                    >
                      {questionnaire.externalId}
                    </ThemeTypography>
                  </FlexBox>
                )}
              </FlexBox>

              <FlexBox mb={1} gap={1}>
                <FlexBox gap={0.5}>
                  <Icon name="tag-filled" color="gray-80" size="small" />
                  <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                    Self-assessment
                  </ThemeTypography>
                </FlexBox>

                <FlexBox gap={0.5}>
                  <Icon name="calendar" size="small" color="gray-80" />
                  <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                    {questionnaire.year}
                  </ThemeTypography>
                </FlexBox>

                {!!questionnaire.requestsSent.length && (
                  <FlexBox gap={0.5}>
                    <Icon name="partner" size="small" color="gray-80" />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {questionnaire.requestsSent
                        .map(request => request.requestedToCompany.name)
                        .join(', ')}
                    </ThemeTypography>
                  </FlexBox>
                )}
              </FlexBox>

              <FlexBox gap={2}>
                <ThemeButton
                  startIcon={<Icon name="edit" color="WHITE" />}
                  size="large"
                  color="PRIMARY"
                  loading={false}
                  onClick={handleEditQuestionnaire}
                >
                  Edit questionnaire
                </ThemeButton>

                <ThemeButton size="large" color="BLUE_ICE" onClick={handlePreviewQuestions}>
                  View questions
                </ThemeButton>

                {/* Show only one of the same buttons at the same time  */}
                {!!pendingQuestionnairesRequests?.length && (
                  <ThemeButton
                    startIcon={<Icon name="document-request-add" size="small" />}
                    size="large"
                    color="BLUE_ICE"
                    onClick={handleCreateQuestionnaireRequest}
                  >
                    New partner request
                  </ThemeButton>
                )}
              </FlexBox>
            </Box>
          </OverviewHeader>
        </Grid>

        <Grid item xs={4} md={5}>
          <Box position="relative">
            <PageSubTitle title="Questionnaire responses" mb={2} />
            <RiskPieChart
              inProgressAmount={questionnaireResponseSummary?.pendingAnswers || 0}
              highRiskAmount={questionnaireResponseSummary?.highRiskAnswers || 0}
              mediumRiskAmount={questionnaireResponseSummary?.mediumRiskAnswers || 0}
              lowRiskAmount={questionnaireResponseSummary?.lowRiskAnswers || 0}
              amountLabel="request"
            />
          </Box>
        </Grid>

        <Grid item xs={8} md={7}>
          <Box position="relative">
            <PageSubTitle
              title="Pending assessment requests"
              visibleItems={pendingQuestionnairesRequests?.length}
              totalItems={pendingQuestionnairesRequests?.length}
              mb={2}
            >
              <FlexBox gap={1}>
                {/* {!!totalSiteActivitiesCount &&
                    totalSiteActivitiesCount > PREVIEW_LATEST_ITEMS && (
                      <SeeAllLink to={`activities`}>See all activities</SeeAllLink>
                    )} */}
                <ThemeButton
                  onClick={handleCreateQuestionnaireRequest}
                  color="WHITE"
                  size="small"
                  startIcon={<Icon name="plus" />}
                >
                  Add new
                </ThemeButton>
              </FlexBox>
            </PageSubTitle>

            {!!pendingQuestionnairesRequests?.length ? (
              <FlexBox gap={1} flexDirection="column">
                {pendingQuestionnairesRequests.map(request => (
                  <QuestionnaireItem
                    key={request.id}
                    requestStatus={request.requestStatus}
                    questionnaire={questionnaire}
                    partnerName={request.requestedToCompany.name}
                    onEdit={() => handleEditRequest(request)}
                    onReviewRequest={() => handleAcceptRequest(request)}
                    onDelete={() => handleDeleteRequest(request)}
                  />
                ))}
              </FlexBox>
            ) : (
              <EmptyStateCard
                variant={EItemTypes.DOCUMENT}
                title="Request partners to complete questionnaires to assess their compliance"
                size={EAvailableSizes.LARGE}
                background={EBackgroundTypes.WORLD}
                buttonText="New partner request"
                buttonIcon={<Icon name="document-request-add" color="white" />}
                onCreateItemClick={handleCreateQuestionnaireRequest}
              />
            )}
          </Box>
        </Grid>

        {hasCompletedResponses && (
          <Grid item xs={12}>
            <Box position="relative">
              <PageSubTitle
                title="Completed partner self-assessments"
                visibleItems={questionnaireResponses.length}
                totalItems={questionnaireResponses.length}
                mb={1}
              >
                <FlexBox gap={1}>
                  <SeeAllLink to={`responses`}>See all answers</SeeAllLink>
                </FlexBox>
              </PageSubTitle>

              <Filter
                filters={filters}
                onFilterChange={handleFilterChange}
                onFilterReset={handleFilterReset}
              />

              {questionnaireResponsesLoading && (
                <Box position="relative" height={400}>
                  <Loader />
                </Box>
              )}

              {!questionnaireResponsesLoading && !questionnaireResponses.length && (
                <Box position="relative" height={400}>
                  <EmptySearchResults
                    message="No results found"
                    skeletonComponent={<PartnerSkeleton />}
                  />
                </Box>
              )}

              {!questionnaireResponsesLoading && !!questionnaireResponses.length && (
                <QuestionnaireResponseTable
                  questionnaire={questionnaire}
                  responses={questionnaireResponses}
                  columns={[
                    EQuestionnaireResponseHeaderColumnEnum.TITLE,
                    EQuestionnaireResponseHeaderColumnEnum.ASSESSED_PARTNER,
                    EQuestionnaireResponseHeaderColumnEnum.DATE_COMPLETED,
                  ]}
                  onResponseClick={({ id }) => handleViewResponse(id)}
                />
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
};

export default QuestionnaireOverview;
